import React, { useState, useEffect, useRef } from 'react';
import './Header.css';

const Header = ({ children, homepageContext = null, activeTab, onTabChange, onLogoClick, loadingComponent, disableScrollShrink = false, forceCollapsed = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const isScrolledRef = useRef(false);
  const expandedHeightRef = useRef(0);

  // Close mobile menu when active tab changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [activeTab]);

  // Prevent background stacking issues when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => document.body.classList.remove('mobile-menu-open');
  }, [isMobileMenuOpen]);

  // Track header height via ResizeObserver so the spacer always matches.
  // Hysteresis in the scroll handler (collapse >100px, expand <30px) prevents
  // the old feedback loop, so we can safely track height in both states.
  useEffect(() => {
    const measureHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight + 20;
        if (!isScrolledRef.current) {
          expandedHeightRef.current = height;
        }
        setHeaderHeight(height);
      }
    };

    measureHeight();

    const resizeObserver = new ResizeObserver(measureHeight);

    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    window.addEventListener('resize', measureHeight);

    return () => {
      window.removeEventListener('resize', measureHeight);
      resizeObserver.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (forceCollapsed) {
      setIsScrolled(true);
      isScrolledRef.current = true;
      return undefined;
    }
    if (disableScrollShrink) {
      setIsScrolled(false);
      isScrolledRef.current = false;
      return undefined;
    }

    // Hysteresis: collapse at 100px, expand only when back below 30px.
    // Prevents rapid toggling when content shifts near the threshold.
    const handleScroll = () => {
      const offset = window.scrollY;
      if (!isScrolledRef.current && offset > 100) {
        isScrolledRef.current = true;
        setIsScrolled(true);
      } else if (isScrolledRef.current && offset < 30) {
        isScrolledRef.current = false;
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [disableScrollShrink, forceCollapsed]);

  // Mobile: keep header visible when keyboard opens on search input
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.innerWidth > 768) return undefined;

    const isSearchInput = (target) => !!target?.closest?.('#header-search-container');

    const handleFocusIn = (event) => {
      if (isSearchInput(event.target)) {
        headerRef.current?.classList.add('keyboard-open');
      }
    };

    const handleFocusOut = () => {
      setTimeout(() => {
        if (!isSearchInput(document.activeElement)) {
          headerRef.current?.classList.remove('keyboard-open');
        }
      }, 0);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // The rights-analysis workspace is a separate path-routed surface (not an
  // activeTab). Navigate there via the History API and let App's popstate
  // listener swap in the workspace shell.
  const goToWorkspace = () => {
    window.history.pushState({}, '', '/projects');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const getHeroTitle = () => {
    switch (activeTab) {
      case 'contact':
        return 'צור קשר';
      case 'feedback':
        return 'משוב והצעות לשיפור';
      case 'about':
        return 'אודות אורבניקס';
      case 'project-promotion':
        return 'קידום פרויקטים';
      case 'rights-dashboard':
        return 'בדיקת זכויות בנייה';
      default:
        return 'כל המידע על הנכס שלך במקום אחד';
    }
  };

  return (
    <>
      {/* Static Spacer - Always present, always same height as expanded header */}
      {/* This prevents layout jumps when header becomes fixed/changes size */}
      <div 
        style={{ 
          height: disableScrollShrink ? 0 : (headerHeight ? headerHeight : 'auto'), 
          minHeight: disableScrollShrink ? 0 : (activeTab === 'rights-dashboard' ? '0px' : (['contact', 'feedback', 'about', 'project-promotion', 'kb-chat'].includes(activeTab) ? '180px' : '300px'))
        }} 
      />
      
      <header 
        ref={headerRef}
        className={`main-header ${(disableScrollShrink && !isMobileMenuOpen) ? 'static-header' : ''} ${isScrolled ? 'scrolled' : ''} ${['contact', 'feedback', 'about', 'project-promotion', 'kb-chat', 'rights-dashboard'].includes(activeTab) ? 'compact-header' : ''} ${isMobileMenuOpen ? 'menu-open' : ''}`}
      >
        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? "סגור תפריט" : "פתח תפריט"}
        >
          <span className={`hamburger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        <div className="header-content">
        <div 
          className="logo-area" 
          onClick={() => onLogoClick ? onLogoClick() : (onTabChange && onTabChange('property'))}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') (onLogoClick ? onLogoClick() : (onTabChange && onTabChange('property'))); }}
          title="חזרה לדף הבית"
        >
          <img src="/urbanix-logo.svg" alt="Urbanix Logo" className="logo" />
        </div>

        {/* Desktop Navigation - Hidden on mobile */}
        <nav className="main-nav desktop-nav">
          <button
            type="button"
            className={`nav-link ${activeTab === 'property' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('property'); }}
          >
            מידע על נכס
          </button>
          <button
            type="button"
            className="nav-link"
            onClick={goToWorkspace}
          >
            ניתוח זכויות
          </button>
          <button
            type="button"
            className={`nav-link ${activeTab === 'kb-chat' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('kb-chat'); }}
          >
            מאגר ידע
          </button>
          <button
            type="button"
            className={`nav-link ${activeTab === 'about' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('about'); }}
          >
            אודות
          </button>
          <button
            type="button"
            className={`nav-link ${activeTab === 'project-promotion' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('project-promotion'); }}
          >
            קידום פרויקטים
          </button>
          <button
            type="button"
            className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('contact'); }}
          >
            צור קשר
          </button>
          <button
            type="button"
            className={`nav-link ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => { onTabChange && onTabChange('feedback'); }}
          >
            משוב
          </button>
        </nav>
      </div>

      {/* Mobile Navigation Overlay - Outside header-content to avoid being hidden */}
      <nav className={`mobile-nav-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
        <button
          type="button"
          className={`nav-link ${activeTab === 'property' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('property'); setIsMobileMenuOpen(false); }}
        >
          מידע על נכס
        </button>
        <button
          type="button"
          className="nav-link"
          onClick={() => { setIsMobileMenuOpen(false); goToWorkspace(); }}
        >
          ניתוח זכויות
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'kb-chat' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('kb-chat'); setIsMobileMenuOpen(false); }}
        >
          מאגר ידע
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('about'); setIsMobileMenuOpen(false); }}
        >
          אודות
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'project-promotion' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('project-promotion'); setIsMobileMenuOpen(false); }}
        >
          קידום פרויקטים
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('contact'); setIsMobileMenuOpen(false); }}
        >
          צור קשר
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => { onTabChange && onTabChange('feedback'); setIsMobileMenuOpen(false); }}
        >
          משוב
        </button>
      </nav>
      
      {/* The rights-check tool has its own sticky header with the title, so the
          shared navy hero is suppressed there — just the nav bar shows. */}
      {activeTab !== 'rights-dashboard' && (
        <div className="hero-search-section">
          <div className="hero-text-group">
            <h1 className="hero-title">
              {getHeroTitle()}
            </h1>
          </div>

          {/* Injected search bar - only show if passed and activeTab isn't contact/feedback */}
          {/* Actually let's rely on parent passing null if needed, but here we just render children */}
          <div id="header-search-container">
              {children}
          </div>
          {homepageContext}
        </div>
      )}
      
      {/* Loading progress - integrated into header border */}
      {loadingComponent && (
        <div className="header-border-loading">
          {loadingComponent}
        </div>
      )}
      </header>
    </>
  );
};

export default Header;
